import React, { useState } from "react";
import { giftProPlayer } from "../lib/api";
import { AvatarChip } from "./AvatarChip";

/**
 * GiftProModal — buyer pays $1.99 via Stripe Checkout, recipient gets 30
 * days of PRO Player perks. On click → opens Stripe → returns to the app
 * with ?gift=success&session_id=... (the Pricing polling path handles the
 * apply; the recipient also gets an inbox notification).
 */
export function GiftProModal({ open, player, onClose, onToast }) {
  const [busy, setBusy] = useState(false);

  if (!open || !player) return null;

  const isAlreadyPro =
    (player.user_type || "").toLowerCase() === "pro" ||
    (player.plan || "").toLowerCase() === "pro" ||
    (player.plan || "").toLowerCase() === "elite";

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await giftProPlayer(player.user_id, window.location.origin);
      if (r?.url) {
        onToast?.(`Opening Stripe checkout — gifting PRO to ${player.name}.`);
        window.location.href = r.url;
      } else {
        onToast?.("Couldn't start checkout. Try again.");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't open checkout.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      data-testid="gift-pro-modal"
      onClick={onClose}
    >
      <div
        className="glass-strong rounded-3xl p-7 w-full max-w-md border-2 border-[#14b8a6]/60"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
          <i className="fas fa-gift mr-1" />GIFT PRO PLAN
        </p>
        <h2 className="font-display text-3xl mt-2 grad-text">$1.99 · 30 DAYS</h2>

        <div className="mt-5 flex items-center gap-3 p-3 rounded-2xl bg-black/40 border border-white/10">
          <AvatarChip user={player} size={48} />
          <div className="min-w-0">
            <p className="text-white font-bold truncate" data-testid="gift-pro-recipient-name">
              {player.name}
            </p>
            <p className="text-[11px] text-white/50">
              {isAlreadyPro
                ? "Already PRO — your gift stacks 30 days on top of their current expiry."
                : "Currently a Casual Player."}
            </p>
          </div>
        </div>

        <ul className="mt-5 space-y-2 text-[12px] text-white/75">
          <li className="flex items-start gap-2">
            <i className="fas fa-check text-[#14b8a6] mt-0.5" />
            <span>Recipient gets the blue verified check + all PRO perks for 30 days.</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="fas fa-check text-[#14b8a6] mt-0.5" />
            <span>One-time charge — no recurring billing on your card.</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="fas fa-check text-[#14b8a6] mt-0.5" />
            <span>They'll see a notice in their inbox crediting you as the gifter.</span>
          </li>
        </ul>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="gift-pro-cancel-btn"
            className="flex-1 text-[11px] tracking-widest px-4 py-3 rounded-xl btn-ghost"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={busy}
            data-testid="gift-pro-confirm-btn"
            className="flex-1 text-[11px] tracking-widest px-4 py-3 rounded-xl bg-[#14b8a6] text-black font-bold hover:brightness-110 disabled:opacity-60"
          >
            {busy ? "OPENING…" : `GIFT $1.99 →`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GiftProModal;
